import { useState, useEffect, useCallback } from "react";
import fleetApi from "@/lib/fleetApi";
import { Panel, PageHeader, Loading } from "@/components/portal/widgets";
import { Input } from "@/components/ui/input";
import ShiftEditDialog, { DAYS, fmtShift } from "@/components/portal/ShiftEditDialog";
import { Search, Pencil } from "lucide-react";

export default function ShiftsPage() {
  const [drivers, setDrivers] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    fleetApi.get("/drivers").then((r) => setDrivers((r.data || []).filter((d) => d.status !== "retired")
      .sort((a, b) => (a.full_name || a.name).localeCompare(b.full_name || b.name)))).catch(() => setDrivers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!drivers) return <Loading />;
  const shown = drivers.filter((d) => (d.full_name || d.name || "").toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div data-testid="shifts-page">
      <PageHeader title="Driver Shifts" subtitle="Weekly hours for each driver. AI only assigns trips inside a driver's shift.">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a driver" className="w-56 border-slate-700 bg-slate-900 pl-8 text-sm text-white" data-testid="shifts-search" />
        </div>
      </PageHeader>
      <Panel testid="shifts-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Driver</th>
                {DAYS.map((d) => <th key={d} className="px-2 py-3">{d.slice(0, 3)}</th>)}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {shown.map((d) => <ShiftRow key={d.id} d={d} onEdit={() => setEdit(d)} />)}
              {!shown.length && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400" data-testid="shifts-empty">No drivers found.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
      <ShiftEditDialog driver={edit} open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }} onSaved={load} />
    </div>
  );
}

function ShiftRow({ d, onEdit }) {
  const t = d.shift_template || {};
  const none = DAYS.every((_, i) => !t[i]);
  return (
    <tr className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`shift-row-${d.id}`}>
      <td className="px-4 py-3">
        <div className="font-semibold text-white">{d.full_name || d.name}</div>
        {none && <div className="text-[11px] text-amber-300" data-testid={`shift-none-${d.id}`}>No shifts set — AI can't assign this driver</div>}
      </td>
      {DAYS.map((_, i) => (
        <td key={i} className={`px-2 py-3 font-mono-data text-xs ${t[i] ? "text-slate-200" : "text-slate-600"}`} data-testid={`shift-cell-${d.id}-${i}`}>{fmtShift(t[i])}</td>
      ))}
      <td className="px-4 py-3 text-right">
        <button onClick={onEdit} data-testid={`shift-edit-${d.id}`}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800/60 px-2 py-1 text-[11px] font-medium text-blue-300 transition-colors hover:bg-slate-700">
          <Pencil className="h-3 w-3" /> Edit
        </button>
      </td>
    </tr>
  );
}
